/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// React
import { useRef, useEffect, useState } from 'react';

// Mantine
import { Drawer, Box, ScrollArea } from '@mantine/core';
import { useForm, isNotEmpty } from '@mantine/form';

// Styles
import classes from './AddConversionFactor.module.css';

// Components
import { PreviousDrawerButton } from '@components/atoms/PreviousDrawerButton';
import { DefaultModal } from '@components/molecules/DefaultModal';

// Components : Steps
import { ConversionFactorForm } from './ConversionFactorForm';

// Store
import useStore from '@store/index';
import { useTranslation } from 'react-i18next';
import { TFormValuesConversionFactor } from '../../types';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
export const AddConversionFactor = (props: {
	onClick?: () => void;
	onClose: () => void;
	opened: boolean;
}) => {
	/* ******************************** STATES ******************************* */
	const [isOpenModalCancel, setIsOpenModalCancel] =
		useState(false);
	/* ******************************** HOOKS *********************************** */
	const viewport = useRef<HTMLDivElement>(null);
	const { t } = useTranslation();

	const {
		offsetCreationActiveStep,
		setOffsetCreationActiveStep,
	} = useStore();

	// Validators
	const form = useForm<TFormValuesConversionFactor>({
		initialValues: {
			project: '',
			site: '',
			organization: '',
			entity: '',
			startDate: null,
			ratio: 0,
			note: '',
		},

		validate: {
			organization: isNotEmpty(
				t('global.organizationRequired')
			),
			site: isNotEmpty(t('global.siteRequired')),
			project: isNotEmpty(t('global.projectRequired')),
			entity: isNotEmpty(t('global.entityRequired')),
			startDate: isNotEmpty(
				t('global.startDateRequired')
			),
			ratio: (value) =>
				value == 0 ? t('global.ratioPositive') : null,
			note: isNotEmpty(t('global.noteRequired')),
		},
	});

	useEffect(() => {
		if (
			offsetCreationActiveStep >= 0 &&
			viewport &&
			viewport.current
		) {
			viewport.current!.scrollTo({
				top: 0,
				behavior: 'smooth',
			});
		}
	}, [offsetCreationActiveStep]);

	useEffect(() => {
		setOffsetCreationActiveStep(0);
	}, []);

	/* ******************************** RENDERING ******************************* */
	return (
		<Drawer.Root
			position="right"
			size="100%"
			opened={props.opened}
			onClose={() => {
				setIsOpenModalCancel(true);
			}}
		>
			<Drawer.Overlay />
			<Drawer.Content
				ref={viewport}
				className={classes.root}
			>
				<DefaultModal
					opened={isOpenModalCancel}
					onConfirm={() => {
						props.onClose();
						setIsOpenModalCancel(false);
						form.reset();
					}}
					onClose={() => {
						setIsOpenModalCancel(false);
					}}
					title={t(
						'conversionFactors.backToAllConversionFactorsSure'
					)}
				/>
				{/** Header */}
				<Drawer.Header className={classes.header}>
					<PreviousDrawerButton
						onClick={() => {
							setIsOpenModalCancel(true);
						}}
					>
						{t(
							'conversionFactors.backToAllConversionFactors'
						)}
					</PreviousDrawerButton>
				</Drawer.Header>
				{/** Body */}
				<Drawer.Body className={classes.body}>
					<Box className={classes.bodySpacer} />
					<Box className={classes.bodyContent}>
						{/** Content */}
						<ScrollArea
							viewportRef={viewport}
							className={classes.containerContent}
						>
							<ConversionFactorForm
								form={form}
								onCancel={() => {
									setIsOpenModalCancel(true);
								}}
								onBack={() => {
									props.onClose();
									form.reset();
								}}
							/>
						</ScrollArea>
					</Box>
					<Box className={classes.bodySpacer} />
				</Drawer.Body>
				{/** Footer */}
				<Box className={classes.containerFooter}>
					<Box className={classes.spacerFooter} />

					<Box className={classes.spacerFooter} />
				</Box>
			</Drawer.Content>
		</Drawer.Root>
	);
};
